import React, { useEffect, useState } from 'react';
import { ArrowTopRightOnSquareIcon, MapPinIcon } from '@heroicons/react/24/outline';
import {
  buildStaticMapUrl,
  formatPlaceHeadline,
  formatPlaceSubline,
  googleMapsDirectionsUrl,
  googleMapsViewUrl,
  hasValidatedPlace,
  type PlaceDisplayInput,
} from '../../utils/placeDomain';
import apiService from '../../services/ApiService';
import { fetchGoogleMapsConfig } from '../../utils/googleMapsLoader';

interface PlaceMapCardProps {
  place: PlaceDisplayInput;
  compact?: boolean;
  className?: string;
}

/** Map preview with directions for a validated Google place or text-only fallback. */
export const PlaceMapCard: React.FC<PlaceMapCardProps> = ({
  place,
  compact = false,
  className = '',
}) => {
  const [apiKey, setApiKey] = useState<string | null>(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || null,
  );

  useEffect(() => {
    if (apiKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const config = await fetchGoogleMapsConfig(async () => apiService.getMapsConfig());
        if (!cancelled && config.maps_api_key) setApiKey(config.maps_api_key);
      } catch {
        // static map optional
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  const headline = formatPlaceHeadline(place);
  const subline = formatPlaceSubline(place);
  const directionsUrl = googleMapsDirectionsUrl(place);
  const viewUrl = googleMapsViewUrl(place);
  const validated = hasValidatedPlace(place);

  if (!headline && !subline) return null;

  const mapUrl =
    validated && apiKey && place.latitude != null && place.longitude != null
      ? buildStaticMapUrl({
          apiKey,
          latitude: place.latitude,
          longitude: place.longitude,
          width: compact ? 480 : 640,
          height: compact ? 200 : 280,
        })
      : null;

  return (
    <section
      className={`rounded-xl border border-white/[0.08] bg-[#0c0c0c]/50 overflow-hidden ${className}`}
      aria-label="Location"
    >
      {mapUrl ? (
        <a
          href={viewUrl ?? directionsUrl ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="block group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60"
        >
          <img
            src={mapUrl}
            alt=""
            className="w-full h-auto object-cover bg-[#111]"
            loading="lazy"
            decoding="async"
          />
          <span className="sr-only">Open location in Google Maps</span>
        </a>
      ) : null}

      <div className={`${compact ? 'p-3' : 'p-4 sm:p-5'} space-y-3`}>
        <div className="flex items-start gap-2.5">
          <MapPinIcon className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            {headline ? <p className="text-sm font-semibold text-white">{headline}</p> : null}
            {subline ? <p className="text-sm text-gray-400 mt-0.5 break-words">{subline}</p> : null}
          </div>
        </div>

        {directionsUrl ? (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary btn-sm inline-flex items-center gap-1.5 cursor-pointer transition-colors duration-200"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden />
            Directions
            <span className="sr-only">(opens Google Maps)</span>
          </a>
        ) : null}
      </div>
    </section>
  );
};

export default PlaceMapCard;
